import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { getCurrentSupplier } from '../../../redux/services/authService';
import { buildSupplierPath, hasCreditAppRole, isCreditAppRestricted } from '../../../utils/helpers';

/**
 * Index for the id-less `/supplier` route. Resolves the active supplier and forwards
 * the user to the correct supplier-side landing based on their roles, mirroring the
 * role-based destinations that `ProtectedRoute` enforces:
 *
 *   - SUPPLIER                      -> `/supplier/:supplierId/dashboardPayments`
 *   - AR_SALES (no AR_ALL)          -> `/supplier/:supplierId/paymentRequests`
 *   - CREDIT_APP_* (no AR role)     -> `/supplier/:supplierId/creditApplications`
 *   - SUB_SUPPLIER (no supplier role) -> `/lienReleases` (no supplier scope needed)
 *
 * `DILL_ADMIN` is ignored when deciding the landing (same as ProtectedRoute), and the
 * supplier id falls back from the persisted selection to the first linked supplier.
 */
export const SupplierIndexRedirect = () => {
  const dispatch = useAppDispatch();
  const { user, currentSupplier } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  const safeRedirectTo =
    redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : null;

  // DILL_ADMIN never drives the landing decision (matches ProtectedRoute's `mainRoles`).
  const roles = (user?.roles ?? []).filter((role) => role !== 'DILL_ADMIN');
  const hasSupplier = roles.includes('SUPPLIER');
  const hasCreditApp = hasCreditAppRole(roles);
  const hasSubSupplier = roles.includes('SUB_SUPPLIER');
  const isArSalesOnly = roles.includes('AR_SALES') && !roles.includes('AR_ALL');
  const isCreditAppOnly = isCreditAppRestricted(user?.roles);

  const isSubSupplierOnly = hasSubSupplier && !hasSupplier && !hasCreditApp;

  const linkedSupplierIds = user?.linkedSupplierIds ?? [];
  const savedSupplierId = localStorage.getItem('selectedSupplierId');
  const supplierId =
    savedSupplierId && linkedSupplierIds.includes(savedSupplierId)
      ? savedSupplierId
      : linkedSupplierIds[0];

  // Sub-supplier-only users have no supplier dashboard scope; the others do.
  const needsSupplierScope = !isSubSupplierOnly && Boolean(supplierId);

  useEffect(() => {
    if (!user) {
      return;
    }
    // `/supplier` is the Supplier account-type entry point, mirror ProtectedRoute.
    localStorage.setItem('selectAccountType', 'Supplier');
    if (needsSupplierScope && supplierId && currentSupplier?.id !== supplierId) {
      dispatch(getCurrentSupplier({ supplierId }));
    }
  }, [user, needsSupplierScope, supplierId, currentSupplier?.id, dispatch]);

  // Sub-supplier-only users have no supplier scope — send them to lien releases.
  if (isSubSupplierOnly) {
    return <Navigate to="/lienReleases" replace />;
  }

  // Remaining supplier-side landings need a supplier id. Without one, a sub-supplier
  // still has lien releases to fall back to; otherwise stay on the shell rather than
  // redirect to a broken, id-less supplier path.
  if (!supplierId) {
    if (hasSubSupplier) {
      return <Navigate to="/lienReleases" replace />;
    }
    return null;
  }

  if (safeRedirectTo) {
    return <Navigate to={buildSupplierPath(supplierId, safeRedirectTo)} replace />;
  }

  // Credit app users can't use the payments dashboard; credit apps is home.
  if (isCreditAppOnly) {
    return <Navigate to={buildSupplierPath(supplierId, '/creditApplications')} replace />;
  }

  if (isArSalesOnly) {
    return <Navigate to={buildSupplierPath(supplierId, '/paymentRequests')} replace />;
  }

  // Full supplier (and any other supplier-linked role) lands on the payments dashboard.
  return <Navigate to={buildSupplierPath(supplierId, '/dashboardPayments')} replace />;
};
